/**
 * PUT a file to S3 using XMLHttpRequest.
 *
 * The shared `http` client was migrated from axios to native `fetch` (#20385).
 * iOS/iPadOS WebKit has a long-standing bug where a cross-origin `fetch()` PUT
 * of a `File`/`Blob` body rejects with the opaque "Load failed" — which is
 * exactly how painter photo uploads broke on iPad (#21104). axios used
 * XMLHttpRequest under the hood in the browser, which is why uploads worked
 * before the migration, so we PUT via XHR here to sidestep the WebKit bug.
 *
 * Browser-only: callers must guard with `typeof XMLHttpRequest !== 'undefined'`
 * and fall back to the fetch client on the server (Node has no XHR).
 */
export default function putFileViaXhr({
  url,
  file,
  headers,
  timeout
}: {
  url: string
  file: File
  headers: Record<string, string>
  timeout: number
}): Promise<{ status: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.timeout = timeout

    for (const [key, value] of Object.entries(headers)) {
      if (value !== undefined && value !== null) xhr.setRequestHeader(key, String(value))
    }

    // Track how far the request body actually got. When an upload fails on iOS
    // WebKit it's otherwise impossible to tell, from the error alone, whether the
    // PUT was blocked before it left the device or whether the bytes reached S3
    // but the response came back blocked. We surface the last progress +
    // readyState both in the message (human-readable, e.g. in Sentry) and as
    // structured `bytesSent`/`bytesTotal`/`readyState` props on the error — those
    // flow through `getUploadFailureContext` into the PostHog
    // `painter_file_upload_failed` event so it can be grouped/filtered without
    // regex. Either way the next repro is conclusive (#21104):
    //  - sent 100% + timeout/error  => request landed at S3, response blocked
    //    (CORS on the response, or a stalled connection) — infra side.
    //  - no/partial progress        => request never left the device (preflight
    //    rejected, CSP/connect-src, offline) — client side.
    let sent = 0
    let total = file.size
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        sent = event.loaded
        total = event.total
      }
    }
    const progress = () => `sent ${sent}/${total} bytes, readyState ${xhr.readyState}`
    const withProgress = <E extends Error>(err: E): E =>
      Object.assign(err, { bytesSent: sent, bytesTotal: total, readyState: xhr.readyState })

    // Include a short slice of the S3 error body (e.g. an <Error> XML payload)
    // so a residual failure reports something actionable instead of "Load failed".
    const detail = () => {
      const body = (xhr.responseText || '').trim().slice(0, 200)
      return body ? `: ${body}` : ''
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status })
      } else {
        reject(withProgress(new Error(`S3 upload failed with status ${xhr.status}${detail()}`)))
      }
    }
    xhr.onerror = () => reject(withProgress(new Error(`S3 upload network error (${progress()})`)))
    xhr.ontimeout = () => {
      // Mirror the fetch client's timeout mapping (http.ts) so downstream
      // handlers treat XHR and fetch timeouts identically.
      const err = withProgress(new Error(`S3 upload timed out after ${timeout}ms (${progress()})`)) as Error & {
        code?: string
      }
      err.code = 'ECONNABORTED'
      reject(err)
    }
    xhr.onabort = () => reject(new Error('S3 upload aborted'))

    xhr.send(file)
  })
}
